<template>
	<div class="tables-macro-section">
		<div class="tables-container" v-if="roomsStore.activeRoom">
			<div class="table-container-wrapper" v-for="table in activeRoomTables" :key="table.room_table_id">
				<div class="table-container" @click="clickTable(table)" :class="{free: !table.openDate, occupied: !!table.openDate && !table.prebillRequested, closing: !!table.openDate && table.prebillRequested, reserved: tablesCurrentReservations.hasOwnProperty(table.room_table_id)}">
					<div class="icons-header">
						<div class="cover-container">
							<div class="cover-icon" v-html="svgStore.get('iconUser')"></div>
							<div class="cover-value">{{ coverValueText(table) }}</div>
						</div>
						<div class="orders-link" v-if="showTableAction(table)" v-html="getOrderLinkIcon(table)" @click.stop="addOrderTable(table)"></div>
					</div>
					<div class="table-name">{{ table.table_name }}</div>
					<div class="order-value">{{ currencyStore.readableCurrencyValue(table.currentTotal) }}</div>
					<div class="table-status-container" :class="{reserved: table.reservations.length > 0}">
						<div class="table-status"><div class="table-status-circle"></div><p>{{ getTableStatusText(table) }}</p></div>
						<div class="reservation-time" v-if="tablesCurrentReservations.hasOwnProperty(table.room_table_id)" @click.stop="roomsOrdersStore.manageReservationId = tablesCurrentReservations[table.room_table_id].reservation_id">
							<div v-if="tablesCurrentReservations[table.room_table_id].status === 'arrived'" class="icon" v-html="svgStore.get('iconProgress')"></div>
							<div v-else-if="tablesCurrentReservations[table.room_table_id].fullDate < new Date()" class="icon" v-html="svgStore.get('iconWarning')"></div>
							<span v-else-if="(tablesCurrentReservations[table.room_table_id].fullDate - new Date()) < 30 * 60 * 1000">{{ tablesCurrentReservations[table.room_table_id].timeUntil }}</span>
							<div v-else class="icon" v-html="svgStore.get('iconClock')"></div>
						</div>
					</div>
				</div>
				<div class="table-bottom" @click="clickTableBottom(table.room_table_id)" :class="{'asked-assistance': askedAssistance(table.room_table_id)}">
					<p>{{ getTableAvailabilityText(table) }}</p>
					<p class="courses" v-if="table.current_course && userStore.currBusiness && userStore.currBusiness.counter_courses">{{ table.current_course ? table.current_course : 1 }}</p>
					<div class="bell-icon" v-html="svgStore.get('iconBell')"></div>
				</div>
			</div>
			<div class="no-room" v-if="activeRoomTables.length === 0">
				<div class="no-room-text">{{ getString('no_elements') }}</div>
				<div class="no-room-subtitle">{{ getString('no_tables_explain') }}</div>
			</div>
		</div>
		<div class="no-room" v-else>
			<div class="no-room-text">{{ getString('no_room_selected') }}</div>
			<div class="no-room-subtitle">{{ getString('no_room_selected_explain') }}</div>
		</div>
	</div>
</template>

<script>
import {useCounterStore, useExperiencesStore, useOrdersStore, useRoomsOrdersStore, useRoomsStore, useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useNotificationStore, useLanguageStore, useSvgStore} from "@/shared/stores";

export default {
	setup() {
		const roomsStore = useRoomsStore();
		const roomsOrdersStore = useRoomsOrdersStore();
		const languageStore = useLanguageStore();
		const svgStore = useSvgStore();
		const counterStore = useCounterStore();
		const userStore = useUserStore();
		const ordersStore = useOrdersStore();
		const notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		const experiencesStore = useExperiencesStore();
		return {roomsStore, languageStore, getString: languageStore.getString, roomsOrdersStore, notificationStore, currencyStore, svgStore, counterStore, userStore, ordersStore, experiencesStore};
	},
	name: "TablesView",
	props: {
		openingTable: Object,
		coversNum: Number,
		payBillTable: Object
	},
	data() {
		return {
			answeringAssistance: false,
			reservationTimeCheck: null,
		}
	},
	computed: {
		activeRoomTables() {
			if(this.roomsStore.activeRoom) {
				let returningActiveTables = this.roomsStore.activeRoom.tables
					.filter(table => {
						if(table.openDate) {
							return this.roomsStore.showOccupiedTables;
						} else {
							return this.roomsStore.showFreeTables;
						}
					})
					.sort((a, b) => {
						if(this.roomsStore.reverseOrder) {
							return b.order_number - a.order_number;
						} else {
							return a.order_number - b.order_number;
						}
					});
				if(this.roomsOrdersStore.askedAssistanceFilter) {
					returningActiveTables = returningActiveTables.filter(table => !!this.roomsOrdersStore.rtcTablesIds[table.room_table_id]);
				}
				return returningActiveTables;
			} else {
				return [];
			}
		},
		tablesCurrentReservations() {
			if(!this.reservationTimeCheck) {
				return {};
			}
			let result = {};
			let oneHourFromNow = new Date(this.reservationTimeCheck);
			oneHourFromNow.setTime(oneHourFromNow.getTime() + 60 * 60 * 1000);
			if(this.roomsStore.activeRoom) {
				this.roomsStore.activeRoom.tables.forEach(table => {
					table.reservations.forEach(reservation => {
						let newRes = null;
						if(reservation.status === "confirmed") {
							if(reservation.fullDate < oneHourFromNow) {
								newRes = reservation;
							}
						} else if(reservation.status === "arrived") {
							newRes = reservation;
						}
						if(newRes) {
							if(result.hasOwnProperty(table.room_table_id)) {
								let competingRes = result[table.room_table_id];
								if(competingRes.fullDate > newRes.fullDate) {
									result[table.room_table_id] = newRes;
								}
							} else {
								result[table.room_table_id] = newRes;
							}
						}
					});
				});
			}
			return result;
		}
	},
	methods: {
		showTableAction(table) {
			return !!table.openDate && this.userStore.currPlans.counter &&
				((this.userStore.checkOrderEditAllowed && table.ordersNum === 1) || this.userStore.checkOrderCreationAllowed);
		},
		getOrderLinkIcon(table) {
			let iconName = '';

			if(table.ordersNum === 1 && !table.allPrepared && this.userStore.checkOrderEditAllowed) {
				iconName  = 'iconEdit';
			}
			else if (this.userStore.checkOrderCreationAllowed) {
				iconName = 'iconPlus';
			}

			return this.svgStore.get(iconName);
		},
		coverValueText(table) {
			if(table.openDate) {
				if(table.coversNum !== null) {
					return table.coversNum + "/" + (table.seat_number ? table.seat_number : 1);
				}
			}
			return table.seat_number ? table.seat_number : 1;
		},
		getTableStatusText(table) {
			if(table.openDate) {
				return table.timeElapsedOpen ? table.timeElapsedOpen : "-";
			} else {
				return this.getString('free_table');
			}
		},
		getTableAvailabilityText(table) {
			if(table.openDate) {
				if(table.prebillRequested) {
					return this.getString('closing');
				} else {
					if(this.userStore.currBusiness && this.userStore.currBusiness.counter_courses) {
						return this.getString('course');
					} else {
						return this.getString('in_progress');
					}
				}
			} else {
				return this.getString('available');
			}
		},
		clickTable(table) {
			this.$emit('table-clicked', table);
		},
		addOrderTable(table) {
			this.$emit('add-order-table', table);
		},
		clickTableBottom(roomTableId) {
			if(!this.answeringAssistance && this.askedAssistance(roomTableId)) {
				this.answeringAssistance = true;
				this.roomsOrdersStore.viewedRoomTableCall(roomTableId, (response) => {
					this.answeringAssistance = false;
				});
			}
		},
		askedAssistance(tableId) {
			return !!this.roomsOrdersStore.rtcTablesIds[tableId];
		},
		updateReservationTimeCheck() {
			this.reservationTimeCheck = new Date();
		}
	},
	mounted() {
		this.updateReservationTimeCheck();
		if(this.userStore.hasReservations) {
			this.reservationInterval = setInterval(this.updateReservationTimeCheck, 1000 * 60);
		}
	},
	beforeUnmount() {
		if(this.reservationInterval) {
			clearInterval(this.reservationInterval);
		}
	}
}
</script>

<style scoped lang="scss">
.tables-macro-section {
	
	.no-room {
		width: calc(100% - var(--orders-padding) * 2);
		background-color: var(--color-bg);
		border-radius: 13px;
		border: 2px dashed $primary;
		padding: 20px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		min-height: 130px;
		gap: 6px;
		margin: var(--orders-padding);
		z-index: 0;
		position: relative;

		.no-room-text {
			font-size: 18px;
			color: $primary;
			font-weight: 700;
		}

		.no-room-subtitle {
			font-size: 14px;
			color: $primary;
			font-weight: 400;
		}
	}

	.tables-container {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		flex-wrap: wrap;
		margin-top: 10px;
		padding-bottom: 20px;
		z-index: 0;
		position: relative;

		.table-container-wrapper {
			--num-items: 10;
			--width-item: 150px;
			width: calc(calc(100% / var(--num-items)));
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			align-items: center;
			margin-top: 10px;

			@media (max-width: 1800px) {
				--num-items: 9;
			}

			@media (max-width: 1670px) {
				--num-items: 8;
			}

			@media (max-width: 1435px) {
				--num-items: 7;
			}

			@media (max-width: 1250px) {
				--num-items: 6;
			}

			@media (max-width: 1070px) {
				--num-items: 5;
			}

			@media (max-width: 850px) {
				--num-items: 4;
			}

			@media (max-width: 670px) {
				--num-items: 3;
			}

			@media (max-width: 500px) {
				--num-items: 2;
			}

			@media (max-width: 340px) {
				--num-items: 1;
			}

			.table-container {
				border-radius: 13px;
				box-shadow: 0 3px 5px var(--color-box-shadow-2);
				display: flex;
				flex-direction: column;
				width: var(--width-item);
				gap: 6px;
				padding: 10px 10px 15px;
				flex-grow: 1;
				flex-shrink: 0;
				cursor: pointer;
				z-index: 10;
				--card-color: var(--color-bg);
				--accent-color: var(--color-primary);
				--gradient-start-color:var(--color-primary);
				background-color: var(--card-color);
				position: relative;

				&.free {
					--card-color: var(--color-table-free);
					--accent-color: var(--color-table-gradient-start-free);
					--gradient-start-color: var(--color-table-gradient-end-free);
				}

				&.occupied {
					--card-color: var(--color-table-occupied);
					--accent-color: var(--color-table-gradient-start-occupied);
					--gradient-start-color: var(--color-table-gradient-end-occupied);
				}

				&.closing {
					--card-color: var(--color-table-closing);
					--accent-color: var(--color-table-gradient-start-closing);
					--gradient-start-color: var(--color-table-gradient-end-closing);
				}

				&.reserved {
					&:after {
						content: "";
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 100%;
						background: linear-gradient(180deg, rgba(7,82,163,0) 0%, var(--color-primary-alpha-4) 60%, var(--color-primary-alpha-8) 100%);
						border-radius: 13px;
						z-index: 1;
					}
				}

				.icons-header {
					display: flex;
					flex-direction: row;
					justify-content: space-between;
					align-items: center;
					margin-bottom: 2px;
					z-index: 5;

					.cover-container {
						display: flex;
						flex-direction: row;
						justify-content: flex-start;
						align-items: center;
						box-shadow: inset 0 0 3px var(--color-box-shadow-3);
						background-color: var(--color-alpha-1);
						border-radius: 7px;
						padding: 9px 8px;
						gap: 5px;

						.cover-icon {
							width: 14px;
							height: 14px;
							display: flex;
							justify-content: center;
							align-items: center;

							&:deep(svg) {
								width: 100%;
								height: 100%;

								.fill {
									fill: var(--color-primary);
								}

								.stroke {
									stroke: var(--color-primary);
								}
							}
						}

						.cover-value {
							font-size: 14px;
							font-weight: 500;
							color: var(--color-primary);
						}
					}

					.orders-link {
						width: 36px;
						height: 36px;
						display: flex;
						justify-content: center;
						align-items: center;
						border: 1px solid var(--color-primary);
						background-color: var(--color-alpha-1);
						border-radius: 7px;
						padding: 9px;

						&:deep(svg) {
							width: 100%;
							height: 100%;

							.fill {
								fill: var(--color-primary);
							}

							.stroke {
								stroke: var(--color-primary);
							}
						}
					}
				}

				.table-name {
					font-size: 18px;
					font-weight: 700;
					text-align: center;
					@include truncateString(1);
					z-index: 5;
				}

				.order-value {
					font-size: 15px;
					text-align: center;
					margin-bottom: 6px;
					z-index: 5;
				}

				.table-status-container {
					display: flex;
					justify-content: center;
					align-items: center;
					z-index: 5;
					min-height: 30px;

					&.reserved {
						justify-content: space-between;
					}

					.reservation-time {
						background-color: var(--color-primary);
						border-radius: 15px;
						height: 30px;
						display: flex;
						justify-content: center;
						align-items: center;
						padding: 0 7px;
						gap: 5px;

						span {
							font-size: 13px;
							color: var(--color-txt-white);
						}

						.icon {
							display: flex;
							justify-content: center;
							align-items: center;
							height: 16px;
							width: 16px;

							:deep(svg) {
								height: 16px;
								width: 16px;

								.fill {
									fill: var(--color-txt-white);
								}

								.stroke {
									stroke: var(--color-txt-white);
								}
							}
						}
					}

					.table-status {
						position: relative;

						.table-status-circle {
							position: absolute;
							left: -3px;
							top: -3px;
							width: calc(100% + 6px);
							height: calc(100% + 6px);
							border-radius: 100px;
							background: linear-gradient(90deg, var(--gradient-start-color) 0%, var(--accent-color) 100%);
							z-index: -1;
						}

						p {
							padding: 0 10px;
							border-radius: 13px;
							background-color: var(--card-color);
							font-size: 15px;
							font-weight: 700;
							color: var(--accent-color);
						}
					}
				}
			}

			.table-bottom {
				display: flex;
				justify-content: space-between;
				align-items: center;
				border-radius: 0 0 13px 13px;
				width: var(--width-item);
				border-left: 1px solid $primary;
				border-right: 1px solid $primary;
				border-bottom: 1px solid $primary;
				margin-top: -13px;
				padding: 18px 10px 5px;
				z-index: 5;
				transition: background-color .3s ease;

				p {
					font-size: 14px;
					font-weight: 500;
					color: $primary;
					@include truncateString(1);
				}

				.courses {
					box-shadow: inset 0 0 3px rgba(7, 82, 163, 0.3);
					background-color: rgba(7, 82, 163, 0.0666666667);
					border-radius: 7px;
					min-width: 30px;
					text-align: center;
				}

				//simple shake and pulse animation
				@keyframes shakePulse {
					0% { transform: scale(1) rotate(0deg); }
					20% { transform: scale(1.1) rotate(5deg); }
					40% { transform: scale(1.3) rotate(-15deg); }
					60% { transform: scale(1.1) rotate(5deg); }
					80% { transform: scale(1) rotate(-5deg); }
					100% { transform: scale(1) rotate(0deg); }
				}
				
				.bell-icon {
					display: none;
					margin-left: auto;
					margin-right: auto;
					animation: shakePulse .5s 2 ease-in-out;
					width: 19px;
					height: 19px;
					
					:deep(svg) {
						width: 100%;
						height: 100%;
						.fill {
							fill: var(--color-bg);
						}
					}
				}

				&.asked-assistance {
					cursor: pointer;
					background-color: $primary;

					.bell-icon {
						pointer-events: none;
						cursor: pointer;
						display: block;
					}
					p, .courses {
						display: none;
					}
				}
			}
		}

		&:after {
			content: "";
			flex-grow: 1;
		}

	}
}
</style>